// App MY CALENDAR — MoPCal porté dans la suite (Core + module calendar).
// v1 : Calendrier + Événements. Données PARTAGÉES avec MoPCal (mêmes documents,
// via le calendrier hérité de l'espace) — aucune migration. Navigation, pub,
// pied de version, connexion, espaces, profil, multiconnexion, IVV : du Core.
import React, { useEffect, useState } from 'react';
import { StatusBar } from 'expo-status-bar';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { CoreProviders, CoquilleApp, EcranFT, EcranProfil, peutEcrire, SourcesElementsProvider, useElementsPossedes, normaliserElement, useAuth, useT, useMenuOngletActif, FonctionsProvider, AideProvider, FONCTIONS_CORE, ApprentissageProvider, BarrePratique, SectionApprentissage, useInterstitielPub } from '@mop/core';
import { ecouterInventairesAccessibles, ecouterPlansAccessibles } from '@mop/feature-inventory';
import { EcranCalendrier, EcranFamille, useCalendrierActif, FONCTIONS_CALENDRIER, AIDES_CALENDRIER, CalendrierActifProvider, SectionCalendriers, SectionAccesCalendriers, PRATIQUES_CALENDRIER, creerCalendrierSuite } from '@mop/feature-calendar';
// Import CIBLÉ (pas le barrel) : seule la section d'accès inventaires/plans
// est embarquée — la gouvernance d'espace couvre TOUTES les ressources.
import SectionAccesInventaire from '@mop/feature-inventory/src/SectionAccesInventaire';

const APP = { id: 'mop-my-calendar', nom: 'MoP Calendar', version: '0.15.11.13', scheme: 'mopmycalendar' };

// Catalogue des fonctions activables (cœur + module calendrier) + aides.
const CATALOGUE = [...FONCTIONS_CORE, ...FONCTIONS_CALENDRIER];
const AIDES = { ...AIDES_CALENDRIER };

const Calendrier = () => { useInterstitielPub('Calendrier'); return <EcranCalendrier mode="edit" ongletCle="Calendrier" />; };

// Auto-provisioning à l'inscription (22/08) : le Core vient de créer l'espace
// par défaut, on y ajoute ICI un premier calendrier — My Calendar est la seule
// app à savoir ce qu'est un calendrier, le Core reste app-agnostique.
const apresInscription = ({ uid, espaceId }) =>
  creerCalendrierSuite(espaceId, { nom: 'Calendrier', emoji: '📅' }, { uid, nom: '' });

// Ce que je POSSÈDE, tous types confondus — c'est ce que le quota de jetons
// borne. Déclaré au niveau RACINE (au-dessus des contextes métier) pour qu'une
// seule et même réponse serve à tous les écrans qui proposent une création.
// Sans ce montage, chaque écran comptait son propre type : l'app annonçait
// « 2/2 » à quelqu'un qui possédait 2 calendriers ET 2 inventaires.
// La REQUETE compte autant que la collection : `inventaires` et `plans` ne sont
// lisibles qu'via `acces array-contains` (leurs regles ne savent pas prouver un
// filtre sur `creePar` — la liste entiere serait refusee). Le tri « a moi » se
// fait ensuite cote client.
const COLLECTIONS_ELEMENTS = [
  { cle: 'calendrier', collection: 'calendriers', filtre: ['creePar', '=='],           emoji: '📅' },
  { cle: 'inventaire', collection: 'inventaires', filtre: ['acces', 'array-contains'], emoji: '📦' },
  { cle: 'plan',       collection: 'plans',       filtre: ['acces', 'array-contains'], emoji: '🗺️' },
];
const SourcesElements = ({ children }) => {
  // `pret` / `erreurs` remontent aussi : un ecran ne doit pas confondre « rien
  // possede » avec « pas encore compte » — sinon il autorise une creation de
  // trop pendant le premier affichage.
  const { sources, pret, erreurs } = useElementsPossedes(COLLECTIONS_ELEMENTS);
  return <SourcesElementsProvider sources={sources} pret={pret} erreurs={erreurs}>{children}</SourcesElementsProvider>;
};

// IVV réservé aux valideurs (appui long sur Profil). Le Profil est enrichi de la
// section « Mes calendriers » (gestion des calendriers de l'espace).
const Profil = () => {
  const { estIVV } = useAuth();   // (user est relu plus bas pour les sources d elements)
  const t = useT();
  useMenuOngletActif('Profil', [
    { label: t('ft.ivvFaitsTechniques'), icone: '🐞', navigateVers: 'IVV' },
  ], estIVV);
  // Éléments gérables de CETTE app : le Core ne connaît pas la collection des
  // calendriers, l'app les lui déclare pour l'écran « Mes éléments ».
  // Sélection des éléments : on propose TOUT ce qui est à moi ou partagé.
  const { calendriersTous: calendriers } = useCalendrierActif();
  const { user } = useAuth();
  // Les jetons couvrent TOUS les types d'éléments : on écoute donc aussi les
  // inventaires et les plans, qui vivent dans feature-inventory.
  const [inventaires, setInventaires] = useState([]);
  const [plans, setPlans] = useState([]);
  useEffect(() => {
    if (!user?.uid) return undefined;
    const a = ecouterInventairesAccessibles(user.uid, setInventaires, () => {});
    const b = ecouterPlansAccessibles(user.uid, setPlans, () => {});
    return () => { a && a(); b && b(); };
  }, [user?.uid]);
  // Vocabulaire CANONIQUE : `normaliserElement` traduit une fois pour toutes
  // (nom/titre, emoji/icone, creePar/ownerUid) — plus de repli au cas par cas.
  const versElement = (doc, type) => {
    const e = normaliserElement(doc, type);
    return {
      id: e.id, nom: e.nom, espaceId: e.espaceId,
      proprietaire: !!user?.uid && e.proprietaire === user.uid,
      peutEcrire: peutEcrire(doc.accesN, user?.uid, e.proprietaire),
    };
  };
  const sourcesElements = [
    { cle: 'calendrier', label: t('profil.mesCalendriers'), emoji: '📅',
      elements: (calendriers || []).map(c => versElement(c, 'calendrier')) },
    { cle: 'inventaire', label: t('inventaire.titre'), emoji: '📦',
      elements: (inventaires || []).map(i => versElement(i, 'inventaire')) },
    { cle: 'plan', label: t('inventaire.plan'), emoji: '🗺️',
      elements: (plans || []).map(p => versElement(p, 'plan')) },
  ].filter(src => src.elements.length > 0);

  return (
    <EcranProfil elementsSources={sourcesElements} sections={[
      { cle: 'calendriers', titre: t('profil.mesCalendriers'), emoji: '📅', position: 'haut',
        sousTitre: t('calendrier.calendriersEspace'), rendu: <SectionCalendriers /> },
      // Accès unifié : qui voit / contribue / gère chaque calendrier — au même
      // endroit que les inventaires et plans (section « Qui voit quoi »).
      { cle: 'acces-calendriers', titre: t('profil.mesCalendriers'), emoji: '📅', acces: true,
        rendu: <SectionAccesCalendriers /> },
      // Gouvernance d'ESPACE : les accès couvrent TOUTES les ressources, pas
      // seulement le scope de l'app — inventaires & plans réglables ici aussi.
      { cle: 'acces-inventaire', titre: t('inventaire.inventairesPlans'), emoji: '📦', acces: true,
        rendu: <SectionAccesInventaire /> },
      // Tutoriel & Mise en Pratique : mécanique Core, contenu du module.
      { cle: 'apprentissage', titre: t('apprentissage.titre'), emoji: '🎓',
        sousTitre: t('apprentissage.tutoriels') + ' · ' + t('apprentissage.misesEnPratique'),
        rendu: <SectionApprentissage /> },
    ]} />
  );
};

// Onglets construits SOUS les providers (accès à t) — labels traduits.
const Racine = () => {
  const t = useT();
  const onglets = [
    { cle: 'Calendrier', label: t('nav.calendrier'), icone: '📅', composant: Calendrier },
    // Famille = collaboration (Demandes + fonctions en cours de migration MoPCal).
    { cle: 'Famille',    label: t('nav.famille'),    icone: '👪', composant: EcranFamille },
    { cle: 'Profil',     label: t('nav.profil'),     icone: '👤', composant: Profil },
    // Écran navigable mais MASQUÉ de la barre (accès IVV réservé, via appui long).
    { cle: 'IVV',        label: t('nav.ivv'),         icone: '🐞', composant: EcranFT, masque: true },
  ];
  return (
    <>
      <CoquilleApp onglets={onglets} avecPub />
      {/* Mise en Pratique en cours : pastille/carte flottante au niveau app. */}
      <BarrePratique />
    </>
  );
};

export default function App() {
  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <CoreProviders app={APP} theme="auto" apresInscription={apresInscription}>
        <SourcesElements>
        <CalendrierActifProvider>
          <FonctionsProvider catalogue={CATALOGUE}>
            <AideProvider contenus={AIDES}>
              <ApprentissageProvider pratiques={PRATIQUES_CALENDRIER}>
                <StatusBar style="auto" />
                <Racine />
              </ApprentissageProvider>
            </AideProvider>
          </FonctionsProvider>
        </CalendrierActifProvider>
        </SourcesElements>
      </CoreProviders>
    </GestureHandlerRootView>
  );
}
